import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TutorialInteractionAnimation } from "./TutorialInteractionAnimation";
import type { TutorialInteractionDetail } from "../../data/tutorialInteractions";
import { ViewerSettings } from "../../types/settings";

type LanguageCode = ViewerSettings["language"];

const TUTORIAL_INTERACTION_COPY: Record<
  LanguageCode,
  {
    close: string;
    howItWorks: string;
    proTip: string;
    avoidThis: string;
    tryIt: string;
    backToTutorial: string;
    replayAnimation: string;
    confirm: string;
  }
> = {
  en: {
    close: "Close interaction tutorial",
    howItWorks: "How this interaction works",
    proTip: "Pro Tip",
    avoidThis: "Avoid This",
    tryIt: "Try It",
    backToTutorial: "Back to Tutorial",
    replayAnimation: "Replay Animation",
    confirm: "Got It",
  },
  es: {
    close: "Cerrar tutorial de interacci\u00f3n",
    howItWorks: "C\u00f3mo funciona esta interacci\u00f3n",
    proTip: "Consejo",
    avoidThis: "Evita esto",
    tryIt: "Pru\u00e9balo",
    backToTutorial: "Volver al tutorial",
    replayAnimation: "Repetir animaci\u00f3n",
    confirm: "Entendido",
  },
  fr: {
    close: "Fermer le tutoriel interactif",
    howItWorks: "Comment cette interaction fonctionne",
    proTip: "Astuce",
    avoidThis: "\u00c0 \u00e9viter",
    tryIt: "Essayez",
    backToTutorial: "Retour au tutoriel",
    replayAnimation: "Relancer l\u2019animation",
    confirm: "Compris",
  },
  de: {
    close: "Interaktions-Tutorial schlie\u00dfen",
    howItWorks: "So funktioniert diese Interaktion",
    proTip: "Profi-Tipp",
    avoidThis: "Das vermeiden",
    tryIt: "Ausprobieren",
    backToTutorial: "Zur\u00fcck zum Tutorial",
    replayAnimation: "Animation wiederholen",
    confirm: "Verstanden",
  },
  zh: {
    close: "\u5173\u95ed\u4ea4\u4e92\u6559\u7a0b",
    howItWorks: "\u6b64\u4ea4\u4e92\u7684\u5de5\u4f5c\u65b9\u5f0f",
    proTip: "\u5c0f\u63d0\u793a",
    avoidThis: "\u907f\u514d\u8fd9\u6837\u505a",
    tryIt: "\u8bd5\u4e00\u4e0b",
    backToTutorial: "\u8fd4\u56de\u6559\u7a0b",
    replayAnimation: "\u91cd\u64ad\u52a8\u753b",
    confirm: "\u77e5\u9053\u4e86",
  },
};

interface TutorialInteractionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  detail: TutorialInteractionDetail | null;
  language?: LanguageCode;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  primaryTextColor?: string;
  secondaryTextColor?: string;
  iconColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

export function TutorialInteractionModal({
  isOpen,
  onClose,
  onBack,
  detail,
  language = "en",
  backgroundColor = "rgba(255, 255, 255, 0.04)",
  backdropBlur = "40px",
  borderColor = "rgba(255, 255, 255, 0.1)",
  boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  primaryTextColor = "rgba(255, 255, 255, 0.95)",
  secondaryTextColor = "rgba(255, 255, 255, 0.7)",
  iconColor = "rgba(255, 255, 255, 0.8)",
  accentColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
}: TutorialInteractionModalProps) {
  const copy =
    TUTORIAL_INTERACTION_COPY[language] ?? TUTORIAL_INTERACTION_COPY.en;
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [replayKey, setReplayKey] = useState(0);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setReplayKey((value) => value + 1);
      const openTimer = window.setTimeout(() => setIsVisible(true), 10);
      return () => window.clearTimeout(openTimer);
    }

    setIsVisible(false);
    const closeTimer = window.setTimeout(() => setShouldRender(false), 300);
    return () => window.clearTimeout(closeTimer);
  }, [isOpen, detail?.id]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const keyTip = detail?.hintLabel ?? detail?.tips?.[0] ?? null;
  const keyWarning = detail?.warnings?.[0] ?? null;

  if (!shouldRender || !detail) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15, 17, 21, 0.72)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        padding: "1.5rem",
        fontFamily,
      }}
      onClick={onClose}
      role="presentation"
      data-oid="dh9o8:x"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-interaction-title"
        aria-describedby="tutorial-interaction-description"
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "relative",
          width: "min(720px, 92vw)",
          maxHeight: "88vh",
          borderRadius: "2rem",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
          transition: "transform 0.3s ease-in-out",
        }}
        data-oid=":qe_nto"
      >
        <button
          ref={closeButtonRef}
          onClick={onClose}
          aria-label={copy.close}
          style={{
            position: "absolute",
            top: "1.5rem",
            right: "1.5rem",
            width: "2.5rem",
            height: "2.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "9999px",
            background: "transparent",
            border: "none",
            color: secondaryTextColor,
            cursor: "pointer",
            transition: "all 0.3s ease",
            zIndex: 2,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
            e.currentTarget.style.color = primaryTextColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = secondaryTextColor;
          }}
          data-oid="cyztj04"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: "1.25rem", fontWeight: 300 }}
            data-oid="jffhss_"
          >
            close
          </span>
        </button>

        <div style={{ padding: "2rem 2rem 1rem 2rem" }} data-oid="as:8wcr">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "0.25rem",
            }}
            data-oid="d0v7giv"
          >
            <span
              className="material-symbols-outlined"
              style={{
                color: accentColor,
                fontSize: "1.5rem",
                fontWeight: 300,
              }}
              data-oid="tsajnxb"
            >
              {detail.icon}
            </span>
            <h2
              id="tutorial-interaction-title"
              style={{
                margin: 0,
                fontSize: "1.25rem",
                fontWeight: 700,
                letterSpacing: "0.025em",
                color: primaryTextColor,
              }}
              data-oid="q3_q5iy"
            >
              {detail.title}
            </h2>
          </div>
          <p
            id="tutorial-interaction-description"
            style={{
              margin: 0,
              fontSize: "0.875rem",
              color: secondaryTextColor,
              lineHeight: 1.6,
            }}
            data-oid="fu97rnd"
          >
            {detail.shortDescription}
          </p>
        </div>

        <div style={{ padding: "0 2rem 1.25rem 2rem" }} data-oid="qjjeml9">
          <TutorialInteractionAnimation
            preset={detail.animationPreset}
            accentColor={accentColor}
            language={language}
            isPlaying={isOpen}
            replayKey={replayKey}
            onReplay={() => setReplayKey((value) => value + 1)}
            data-oid="37x_n3y"
          />
        </div>

        <div
          style={{
            padding: "0 2rem 1.5rem 2rem",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
          data-oid="ok::l.b"
        >
          <div
            style={{
              padding: "1rem",
              borderRadius: "1rem",
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.06)",
            }}
            data-oid="ie-r6fd"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.85rem",
                color: primaryTextColor,
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
              data-oid="a6oddv9"
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "1rem", color: accentColor }}
                data-oid="e1u7s4n"
              >
                info
              </span>
              {copy.howItWorks}
            </div>
            <div
              style={{
                fontSize: "0.8125rem",
                lineHeight: 1.65,
                color: secondaryTextColor,
                marginBottom: "1rem",
              }}
              data-oid="-wqyqpv"
            >
              {detail.detailedDescription}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
              data-oid="0d:pjd."
            >
              {detail.steps.map((step, index) => (
                <div
                  key={step.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2rem 1fr",
                    gap: "0.75rem",
                    alignItems: "start",
                  }}
                  data-oid="nc.0-a-"
                >
                  <div
                    style={{
                      width: "2rem",
                      height: "2rem",
                      borderRadius: "999px",
                      background: `${accentColor}1A`,
                      color: primaryTextColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                    }}
                    data-oid="scbq1y2"
                  >
                    {index + 1}
                  </div>
                  <div data-oid="121aa.:">
                    <div
                      style={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: primaryTextColor,
                        marginBottom: "0.2rem",
                      }}
                      data-oid="-ln9b:x"
                    >
                      {step.title}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        lineHeight: 1.6,
                        color: secondaryTextColor,
                      }}
                      data-oid="pr7..g2"
                    >
                      {step.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(keyTip || keyWarning || detail.practicePrompt) && (
            <div
              style={{
                padding: "1rem 1.1rem",
                borderRadius: "1rem",
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.06)",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
              data-oid=".itrcmm"
            >
              {keyTip && (
                <InlineCallout
                  icon="lightbulb"
                  label={copy.proTip}
                  text={keyTip}
                  accentColor={accentColor}
                  primaryTextColor={primaryTextColor}
                  secondaryTextColor={secondaryTextColor}
                  data-oid="wsx6xfl"
                />
              )}

              {keyWarning && (
                <InlineCallout
                  icon="warning"
                  label={copy.avoidThis}
                  text={keyWarning}
                  accentColor="rgb(251, 191, 36)"
                  primaryTextColor={primaryTextColor}
                  secondaryTextColor={secondaryTextColor}
                  data-oid=".t88801"
                />
              )}

              {detail.practicePrompt && (
                <InlineCallout
                  icon="sports_handball"
                  label={copy.tryIt}
                  text={detail.practicePrompt}
                  accentColor={accentColor}
                  primaryTextColor={primaryTextColor}
                  secondaryTextColor={secondaryTextColor}
                  data-oid="lm0:2e_"
                />
              )}
            </div>
          )}
        </div>

        <div
          style={{
            padding: "1.5rem 2rem 2rem 2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            borderTop: "1px solid rgba(255, 255, 255, 0.05)",
          }}
          data-oid="ab74chb"
        >
          <button
            onClick={onBack ?? onClose}
            style={{
              background: "transparent",
              border: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: secondaryTextColor,
              cursor: "pointer",
              transition: "color 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = primaryTextColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = secondaryTextColor;
            }}
            data-oid="-bpa5vf"
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: "1rem" }}
              data-oid="ieul0o."
            >
              arrow_back
            </span>
            {copy.backToTutorial}
          </button>

          <div
            style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            data-oid="oufw8fl"
          >
            <button
              onClick={() => setReplayKey((value) => value + 1)}
              style={{
                padding: "0.75rem 1rem",
                borderRadius: "9999px",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                background: "rgba(255, 255, 255, 0.04)",
                color: primaryTextColor,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.45rem",
                fontSize: "0.75rem",
                fontWeight: 600,
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.04)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.12)";
              }}
              data-oid="p48r5dv"
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "1rem" }}
                data-oid="5:bnfsw"
              >
                replay
              </span>
              {copy.replayAnimation}
            </button>

            <button
              onClick={onClose}
              style={{
                padding: "0.75rem 1.5rem",
                fontSize: "0.75rem",
                fontWeight: 700,
                borderRadius: "9999px",
                background: accentColor,
                color: "white",
                border: "none",
                cursor: "pointer",
                boxShadow: `0 0 20px ${accentColor}4D`,
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = `0 8px 25px ${accentColor}66`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = `0 0 20px ${accentColor}4D`;
              }}
              data-oid="41utl:r"
            >
              {copy.confirm}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function InlineCallout({
  icon,
  label,
  text,
  accentColor,
  primaryTextColor,
  secondaryTextColor,
}: {
  icon: string;
  label: string;
  text: string;
  accentColor: string;
  primaryTextColor: string;
  secondaryTextColor: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.5rem 5.75rem 1fr",
        gap: "0.75rem",
        alignItems: "start",
      }}
      data-oid="dxfop9g"
    >
      <span
        className="material-symbols-outlined"
        style={{ fontSize: "1rem", color: accentColor, marginTop: "0.05rem" }}
        data-oid="ugdwsrb"
      >
        {icon}
      </span>
      <div
        style={{
          fontSize: "0.75rem",
          fontWeight: 700,
          color: primaryTextColor,
        }}
        data-oid="syt8pzn"
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "0.75rem",
          color: secondaryTextColor,
          lineHeight: 1.65,
        }}
        data-oid="chyg5xb"
      >
        {text}
      </div>
    </div>
  );
}
